import React from 'react';
import { makeStyles } from '@mui/styles';
import { Box, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';

const NAVY = '#2E3558';
const RED = '#DC2626';
const GREEN = '#16A34A';
const BLUE = '#2563EB';

const S3_BASE_URL = process.env.REACT_APP_ASSETS_URL;

const VARUN_IMAGE_URL = `${S3_BASE_URL}/images/Varun+Lalwani.png`;

const useStyles = makeStyles((theme) => ({
  container: {
    width: '100%',
    background: theme.palette.shades['clr-white-900'],
    padding: theme.spacing(20, 37.5),
    [theme.breakpoints.down('xl')]: {
      padding: theme.spacing(20, 15),
    },
    [theme.breakpoints.down('lg')]: {
      padding: theme.spacing(15, 15),
    },
    [theme.breakpoints.down('md')]: {
      padding: theme.spacing(12, 10),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  contentSection: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(15),
    [theme.breakpoints.down('lg')]: {
      gap: theme.spacing(10),
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: theme.spacing(10),
    },
  },

  /* ---------------- Left column : profile / testimonial ---------------- */
  leftColumn: {
    flex: '0 0 300px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    [theme.breakpoints.down('md')]: {
      flex: '0 0 auto',
      width: '100%',
      maxWidth: '360px',
    },
  },
  avatar: {
    width: '88px',
    height: '88px',
    borderRadius: '50%',
    background: '#1E2A5A',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    boxShadow: '0 8px 20px rgba(30, 42, 90, 0.25)',
    // Keeps the photo inside the circle instead of overflowing its corners.
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  profileName: {
    ...theme.typography['h6-bold'],
    color: NAVY,
    textAlign: 'center',
  },
  profileRole: {
    ...theme.typography['body02-regular'],
    color: theme.palette.neutral['clr-500'],
    textAlign: 'center',
    marginTop: theme.spacing(-1.5),
  },
  improvedBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    background: '#F0FDF4',
    border: '1px solid #BBF7D0',
    borderRadius: '20px',
    padding: theme.spacing(1, 2.5),
  },
  improvedBadgeText: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '12px',
    fontWeight: 700,
    color: GREEN,
  },
  scoreCards: {
    display: 'flex',
    gap: theme.spacing(2.5),
    width: '100%',
    marginTop: theme.spacing(1),
  },
  scoreCard: {
    flex: 1,
    borderRadius: '12px',
    padding: theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  scoreCardDay: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.8px',
  },
  scoreCardValue: {
    fontFamily: '"Roboto Flex", sans-serif',
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: 1.1,
  },
  scoreCardAttempt: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '10px',
    fontWeight: 400,
    color: theme.palette.neutral['clr-500'],
  },
  quoteBox: {
    width: '100%',
    background: '#EEF1FB',
    border: '1px solid #E0E7FF',
    borderRadius: '12px',
    padding: theme.spacing(3.5),
    marginTop: theme.spacing(1),
  },
  quoteIcon: {
    color: '#A5B0E0',
    fontSize: '28px !important',
    transform: 'rotate(180deg)',
    marginBottom: theme.spacing(1),
  },
  quoteText: {
    ...theme.typography['body02-regular'],
    color: theme.palette.neutral['clr-600'],
    fontStyle: 'italic',
    lineHeight: 1.7,
  },
  quoteAuthor: {
    ...theme.typography['body02-bold'],
    color: NAVY,
    marginTop: theme.spacing(2.5),
  },

  /* ---------------- Right column : story + comparison ---------------- */
  rightColumn: {
    flex: 1,
    minWidth: 0,
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    background: '#EEF1FF',
    borderRadius: '20px',
    padding: theme.spacing(1, 2.5),
    marginBottom: theme.spacing(4),
  },
  badgeDot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    background: GREEN,
  },
  badgeText: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: '#4F66E0',
  },
  heading: {
    ...theme.typography['h2-bold'],
    color: NAVY,
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('lg')]: {
      ...theme.typography['h3-bold'],
    },
  },
  headingHighlight: {
    color: GREEN,
    fontStyle: 'italic',
  },
  subHeading: {
    ...theme.typography['body01-regular'],
    color: theme.palette.neutral['clr-600'],
    lineHeight: 1.6,
    marginBottom: theme.spacing(6),
    maxWidth: '560px',
  },
  comparisonGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  comparisonCard: {
    background: theme.palette.shades['clr-white-900'],
    border: '1px solid #E4E7EC',
    borderRadius: '14px',
    padding: theme.spacing(4),
    boxShadow: '0 4px 16px rgba(16, 24, 40, 0.04)',
  },
  comparisonPeriod: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    marginBottom: theme.spacing(2),
  },
  // Day-1 / Day-15 product screenshots (title, score bar, proctorship check
  // all live inside these images).
  comparisonImage: {
    width: '100%',
    height: 'auto',
    display: 'block',
    borderRadius: '8px',
    border: '1px solid #EAECF0',
  },

  /* stats row */
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, 1fr)',
    },
  },
  statCard: {
    background: '#F4F5FA',
    borderRadius: '12px',
    padding: theme.spacing(3.5, 2),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  statValue: {
    fontFamily: '"Roboto Flex", sans-serif',
    fontSize: '26px',
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontFamily: '"Mulish", sans-serif',
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    color: theme.palette.neutral['clr-500'],
  },
}));

const comparisons = [
  {
    period: 'DAY 1 · ATTEMPT 1',
    periodColor: RED,
    image: `${S3_BASE_URL}/images/screenshot_day1_HD.png`,
    alt: 'Day 1 attempt - Daily Snapchat Streak Volume scored 4.67',
  },
  {
    period: 'DAY 15 · ATTEMPT 2',
    periodColor: GREEN,
    image: `${S3_BASE_URL}/images/screenshot_day15_HD.png`,
    alt: 'Day 15 attempt - Indian Combat Drone Market Sizing scored 7.97',
  },
];

const stats = [
  { value: '15', label: 'Days', color: NAVY },
  { value: '15', label: 'Cases Done', color: NAVY },
  { value: '+3.3', label: 'Points Gained', color: BLUE },
  { value: '71%', label: 'Improvement', color: GREEN },
];

const CaseSuccessStory = () => {
  const classes = useStyles();

  return (
    <Box className={classes.container}>
      <Box className={classes.contentSection}>
        {/* Left column - profile & testimonial */}
        <Box className={classes.leftColumn}>
          <Box className={classes.avatar}>
            <img
              src={VARUN_IMAGE_URL}
              alt="Varun Lalwani"
              className={classes.avatarImage}
              loading="lazy"
            />
          </Box>
          <Typography className={classes.profileName}>Varun Lalwani</Typography>
          <Typography className={classes.profileRole}>
            IIT Bombay · Strategy Roles Prep
          </Typography>

          <Box className={classes.improvedBadge}>
            <CheckRoundedIcon sx={{ fontSize: '14px', color: GREEN }} />
            <Typography className={classes.improvedBadgeText}>
              Score improved by 71%
            </Typography>
          </Box>

          <Box className={classes.scoreCards}>
            <Box
              className={classes.scoreCard}
              sx={{ background: '#FEF2F2', border: '1px solid #FECACA' }}
            >
              <Typography className={classes.scoreCardDay} sx={{ color: RED }}>
                DAY 1
              </Typography>
              <Typography className={classes.scoreCardValue} sx={{ color: RED }}>
                4.67
              </Typography>
              <Typography className={classes.scoreCardAttempt}>Attempt 1</Typography>
            </Box>
            <Box
              className={classes.scoreCard}
              sx={{ background: '#F0FDF4', border: '1px solid #BBF7D0' }}
            >
              <Typography className={classes.scoreCardDay} sx={{ color: GREEN }}>
                DAY 15
              </Typography>
              <Typography className={classes.scoreCardValue} sx={{ color: GREEN }}>
                7.97
              </Typography>
              <Typography className={classes.scoreCardAttempt}>Attempt 2</Typography>
            </Box>
          </Box>

          <Box className={classes.quoteBox}>
            <FormatQuoteIcon className={classes.quoteIcon} />
            <Typography className={classes.quoteText}>
              Hi. I am Varun Lalwani from IIT Bombay preparing for strategy roles. Earlier,
              I used to fumble and could not structure my thoughts during case interviews,
              but Casemaster AI gave me real case frameworks and simulations and also the
              feedback which helped me increase my confidence massively.
            </Typography>
            <Typography className={classes.quoteAuthor}>
              — Varun Lalwani, IIT Bombay
            </Typography>
          </Box>
        </Box>

        {/* Right column - story, comparison screenshots & stats */}
        <Box className={classes.rightColumn}>
          <Box className={classes.badge}>
            <Box className={classes.badgeDot} />
            <Typography className={classes.badgeText}>
              Real Student · Real Results
            </Typography>
          </Box>

          <Typography className={classes.heading}>
            From First Attempt to{' '}
            <span className={classes.headingHighlight}>Strong Performance</span> in 15 days
            with Case Master AI
          </Typography>

          <Typography className={classes.subHeading}>
            Varun practised one case every day on Case Master. The AI feedback after each
            session pinpointed exactly where marks were lost — and he fixed every gap, one
            case at a time.
          </Typography>

          <Box className={classes.comparisonGrid}>
            {comparisons.map((item) => (
              <Box key={item.period} className={classes.comparisonCard}>
                <Typography
                  className={classes.comparisonPeriod}
                  sx={{ color: item.periodColor }}
                >
                  {item.period}
                </Typography>
                <img src={item.image} alt={item.alt} className={classes.comparisonImage} />
              </Box>
            ))}
          </Box>

          <Box className={classes.statsRow}>
            {stats.map((stat) => (
              <Box key={stat.label} className={classes.statCard}>
                <Typography className={classes.statValue} sx={{ color: stat.color }}>
                  {stat.value}
                </Typography>
                <Typography className={classes.statLabel}>{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default CaseSuccessStory;
